/**
 * Grab-and-pan for the app's scroll regions - report tables above all.
 *
 * The scrollbars are painted only while a region is being scrolled
 * (src/styles/scrollbars.css), so a table that is wider than its widget has no
 * standing cue and no obvious way to move: the wheel scrolls vertically and
 * shift+wheel is not something a user goes looking for. This supplies the way
 * to move - press anywhere in the table body and drag, in either axis, the way
 * a map pans. Dragging scrolls, which reveals the thumb, which is the cue.
 *
 * One delegated listener on the document, same as scrollAutoHide: scroll
 * regions rendered later are covered without any wiring.
 *
 * Notes on the details that matter:
 *   - nothing happens until the pointer has travelled DRAG_THRESHOLD_PX, so a
 *     plain click on a drill-down link is still a click
 *   - once it is a drag, the click that follows the release is swallowed, so
 *     panning across a link never navigates
 *   - presses that start in a form control, a button or on the scrollbar lane
 *     itself are left alone
 */

const SCROLLABLE_SELECTOR = [
  ".ant-table-content",
  ".ant-table-body",
  "[data-drag-scroll]",
].join(",");

/* Controls that own their own pointer behaviour - text selection, sliders,
   resize handles - must not be turned into pan surfaces.

   .rg-resize-handle is the report table's column-width grip
   (src/components/ReportGrid/ResizableTitle.js); .react-resizable-handle is
   react-grid-layout's own widget handle in the editor. Both are drags of their
   own, and panning the table underneath them would fight the drag. */
const IGNORED_SELECTOR =
  "input,textarea,select,button,[contenteditable='true'],[role='slider'],.ant-table-column-sorter,.react-resizable-handle,.rg-resize-handle";

const DRAG_THRESHOLD_PX = 4;
const READY_CLASS = "drag-scroll-ready"; // cursor: grab
const ACTIVE_CLASS = "drag-scroll-active"; // cursor: grabbing, no text selection

// The region being panned, plus where the press started. The two axes can resolve to
// different elements - see findAxisScroller.
let panEl = null;
let panXEl = null;
let panYEl = null;
let startX = 0;
let startY = 0;
let startScrollLeft = 0;
let startScrollTop = 0;
let dragging = false; // past the threshold
let pointerId = null;

// The element currently wearing READY_CLASS, so it can be cleaned off.
let readyEl = null;

function scrollsX(el) {
  return el.scrollWidth - el.clientWidth > 1;
}

function scrollsY(el) {
  return el.scrollHeight - el.clientHeight > 1;
}

function canScroll(el) {
  return scrollsX(el) || scrollsY(el);
}

/* An element only scrolls if its own overflow says so - scrollHeight alone is true of
   any element with more content than box, scroller or not. */
function hasScrollableOverflow(el, axis) {
  const style = window.getComputedStyle(el);
  const value = axis === "x" ? style.overflowX : style.overflowY;
  return value === "auto" || value === "scroll";
}

/** The nearest pannable ancestor that actually has somewhere to scroll. */
function findPannable(node) {
  let el = node;
  while (el && el.nodeType === 1) {
    if (el.matches && el.matches(SCROLLABLE_SELECTOR) && canScroll(el)) {
      return el;
    }
    el = el.parentElement;
  }
  return null;
}

/**
 * The element a drag should move along one axis.
 *
 * Usually that is the table body for both axes. But a table can be wider than its
 * widget while the rows fit, with the page below it holding the vertical overflow -
 * and then a downward drag has to reach past the table to find the thing that
 * actually scrolls, or it does nothing at all. So each axis is resolved separately,
 * starting at the pannable element and walking up.
 */
function findAxisScroller(from, axis) {
  const overflows = axis === "x" ? scrollsX : scrollsY;
  let el = from;
  while (el && el.nodeType === 1) {
    if (overflows(el) && hasScrollableOverflow(el, axis)) return el;
    el = el.parentElement;
  }
  return null;
}

function setReady(el) {
  if (readyEl === el) return;
  if (readyEl) readyEl.classList.remove(READY_CLASS);
  readyEl = el;
  if (readyEl) readyEl.classList.add(READY_CLASS);
}

/* The grab cursor is only honest on a region with somewhere to go, and whether
   it has depends on the data and the column widths - so it is decided here, per
   pointer move, rather than declared in CSS. */
function handlePointerOver(event) {
  if (panEl) return;
  setReady(findPannable(event.target));
}

function handlePointerDown(event) {
  if (event.button !== 0) return;
  if (event.pointerType === "touch") return; // native touch panning is better

  const target = event.target;
  if (!target || target.nodeType !== 1) return;
  if (target.closest && target.closest(IGNORED_SELECTOR)) return;

  const el = findPannable(target);
  if (!el) return;

  // A press on the scrollbar lane lands on the element but outside its padding
  // box; that is a real scrollbar drag, leave it to the browser.
  if (event.target === el) {
    if (event.offsetX > el.clientWidth || event.offsetY > el.clientHeight) {
      return;
    }
  }

  panEl = el;
  panXEl = findAxisScroller(el, "x");
  panYEl = findAxisScroller(el, "y");
  pointerId = event.pointerId;
  startX = event.clientX;
  startY = event.clientY;
  startScrollLeft = panXEl ? panXEl.scrollLeft : 0;
  startScrollTop = panYEl ? panYEl.scrollTop : 0;
  dragging = false;
}

function handlePointerMove(event) {
  if (!panEl || event.pointerId !== pointerId) return;

  const dx = event.clientX - startX;
  const dy = event.clientY - startY;

  if (!dragging) {
    if (Math.abs(dx) < DRAG_THRESHOLD_PX && Math.abs(dy) < DRAG_THRESHOLD_PX) {
      return;
    }
    dragging = true;
    panEl.classList.add(ACTIVE_CLASS);
    document.documentElement.classList.add(ACTIVE_CLASS);

    // The press has already begun a selection by now; drop it, or the drag
    // paints half the table blue.
    const selection = window.getSelection && window.getSelection();
    if (selection && selection.removeAllRanges) selection.removeAllRanges();

    // Keeps the pan alive when the pointer leaves the table mid-drag.
    if (panEl.setPointerCapture) {
      try {
        panEl.setPointerCapture(pointerId);
      } catch (e) {
        /* capture is a nicety; the window-level listeners still work */
      }
    }
  }

  // Content follows the hand: drag left and the columns to the right come in.
  if (panXEl) panXEl.scrollLeft = startScrollLeft - dx;
  if (panYEl) panYEl.scrollTop = startScrollTop - dy;
}

function endPan() {
  if (!panEl) return;

  if (pointerId !== null && panEl.releasePointerCapture) {
    try {
      panEl.releasePointerCapture(pointerId);
    } catch (e) {
      /* already released with the pointer */
    }
  }

  panEl.classList.remove(ACTIVE_CLASS);
  document.documentElement.classList.remove(ACTIVE_CLASS);
  panEl = null;
  panXEl = null;
  panYEl = null;
  pointerId = null;
  // `dragging` is left set for handleClick, which runs right after this and
  // clears it.
}

function handlePointerUp() {
  endPan();
}

/** Swallows the click that closes a drag, so panning never follows a link. */
function handleClick(event) {
  if (!dragging) return;
  dragging = false;
  event.preventDefault();
  event.stopPropagation();
}

/** HTML5 drag of a link or selected text would fight the pan. */
function handleDragStart(event) {
  if (panEl) event.preventDefault();
}

/**
 * Starts the listeners. Returns a cleanup function.
 */
export default function initDragToScroll() {
  document.addEventListener("pointerover", handlePointerOver, {
    capture: true,
    passive: true,
  });
  document.addEventListener("pointerdown", handlePointerDown, {
    capture: true,
    passive: true,
  });
  document.addEventListener("pointermove", handlePointerMove, {
    capture: true,
  });
  window.addEventListener("pointerup", handlePointerUp, { passive: true });
  window.addEventListener("pointercancel", handlePointerUp, { passive: true });
  document.addEventListener("click", handleClick, { capture: true });
  document.addEventListener("dragstart", handleDragStart, { capture: true });

  return () => {
    document.removeEventListener("pointerover", handlePointerOver, {
      capture: true,
    });
    document.removeEventListener("pointerdown", handlePointerDown, {
      capture: true,
    });
    document.removeEventListener("pointermove", handlePointerMove, {
      capture: true,
    });
    window.removeEventListener("pointerup", handlePointerUp);
    window.removeEventListener("pointercancel", handlePointerUp);
    document.removeEventListener("click", handleClick, { capture: true });
    document.removeEventListener("dragstart", handleDragStart, {
      capture: true,
    });
  };
}
